import { useState, useEffect, useRef } from "react";
import { Keyboard } from "@capacitor/keyboard";
import apiClient from "../utils/apiClient";
import TextareaAutosize from 'react-textarea-autosize';
import Markdown from "../components/Markdown";
import RecipeMessage from "../components/RecipeMessage";
import { useParams, useNavigate } from "react-router-dom";
import { useLoading } from "../contexts/LoadingContext";
import IngredientSelectMessage from "../components/IngredientSelectMessage";
import { PhotoIcon, CameraIcon, PaperAirplaneIcon, ArrowPathIcon } from "@heroicons/react/24/solid";
import { Capacitor } from '@capacitor/core';
import { useAndroidKeyboardOffset } from '../hooks/useAndroidKeyboardOffset';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

export default function ChatPage() {
  const { setSuppressLoader } = useLoading();
  const { id: chatId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem("chatMessages");
    return saved ? JSON.parse(saved) : [];
  });

  const [conversationId, setConversationId] = useState(() => chatId || localStorage.getItem("chatConversationId") || null);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);
  const lastMessageRef = useRef(null);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [uploadMode, setUploadMode] = useState(null); // 'ingredients' or 'recipe'
  const [uploadIntent, setUploadIntent] = useState(null); // 'gallery' or 'camera' // ✅ new
  const [isImageLoading, setIsImageLoading] = useState(false);
  const imageLoaderRef = useRef(null);
  const keyboardOffset = useAndroidKeyboardOffset();
  const [warning, setWarning] = useState(null);

  useEffect(() => {
    setSuppressLoader(true);
    return () => setSuppressLoader(false);
  }, [setSuppressLoader]);

  useEffect(() => {
    apiClient.get(`/users/preferences`).then(res => setUser(res.data));
  }, []);

  useEffect(() => {
    if (user && messages.length === 0) {
      const welcome = {
        role: "model",
        content: `👋 Hey ${user.name}! What would you like help with today?`,
        animated: true
      };
      setMessages([welcome]);
      localStorage.setItem("chatMessages", JSON.stringify([welcome]));
    }
  }, [user]);

  useEffect(() => {
    localStorage.setItem("chatMessages", JSON.stringify(messages));
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [messages]);

  useEffect(() => {
    if (isImageLoading && imageLoaderRef.current) {
      imageLoaderRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isImageLoading]);

  useEffect(() => {
    if (chatId) {
      apiClient.get(`/conversations/${chatId}`).then(res => {
        setMessages(res.data.messages);
        setConversationId(res.data._id);
        localStorage.removeItem("chatMessages"); //NEW
        localStorage.setItem("chatConversationId", res.data._id); //NEW
      }).catch(() => navigate("/chat"));
    } else {
      const savedMessages = localStorage.getItem("chatMessages");
      const savedId = localStorage.getItem("chatConversationId");
      if (savedMessages) setMessages(JSON.parse(savedMessages));
      if (savedId) setConversationId(savedId);
    }
  }, [chatId]);

  useEffect(() => {
    if (conversationId) {
      localStorage.setItem("chatConversationId", conversationId);
    } else {
      localStorage.removeItem("chatConversationId");
    }
  }, [conversationId]);

  const sendMessage = async (textOverride) => {
    const text = textOverride ?? input;
    if (typeof text !== "string" || !text.trim()) return;
    const newMessages = [...messages, { role: "user", content: text }];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);
    setWarning(null);

    try {
      const res = await apiClient.post(`/chat`, { messages: newMessages, conversationId }, {params: {responseVersion:"v0"}});
      if (res.data.conversationId) {
        setConversationId(res.data.conversationId);
        if (!chatId) navigate(`/chat/${res.data.conversationId}`);
      }
      const assistantMessages = res.data.messages;
      const updatedMessages = [...newMessages, ...assistantMessages];
      setMessages(updatedMessages);
    } catch (err) {
      console.error("Chat error:", err);
      if (err.response?.status === 403) {
        setWarning("You've reached your weekly free recipe limit. Upgrade to pro for unlimited recipes and usage.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetConversation = () => {
    setMessages([]);
    setInput("");
    setConversationId(null);
    localStorage.removeItem("chatMessages");
    navigate("/chat");
  };

  const handleImageUpload = async (file, mode) => {
    if (!file || !mode) return;
    setIsImageLoading(true);
    const formData = new FormData();
    formData.append("image", file);
    const endpoint = mode === "ingredients" ? "/chat/image-to-ingredients" : "/chat/image-to-recipe";
    try {
      const res = await apiClient.post(endpoint, formData);
      //Update the conversation ID
      if (res.data.conversationId) {
        setConversationId(res.data.conversationId);
        if (!chatId) navigate(`/chat/${res.data.conversationId}`);
      }
      if (mode === "ingredients") {
        const ingredients = res.data.ingredients;
        const imageContents = res.data.messages[0].content;
        setMessages((prev) => [...prev, { role: "image", content: imageContents }, { role: "ingredient-select", ingredients }]);
      } else if (mode === "recipe") {
        const assistantMessages = res.data.messages;
        setMessages((prev) => [...prev, ...assistantMessages]);
      }
    } catch (err) {
      console.error(`Image ${mode} detection failed:`, err);
      if (err.response?.status === 403) {
        setWarning("You've reached your free weekly recipe limit. Subscribe for unlimited recipes and usage.");
      }
    } finally {
      setIsImageLoading(false);
    }
  };

  const handleUserModeSelection = async (mode) => { // ✅ new
    setUploadMode(mode);
    setShowUploadOptions(false);
    if (uploadIntent === 'gallery') {
      document.getElementById('image-upload').click();
    } else if (uploadIntent === 'camera') {
      try {
        const photo = await Camera.getPhoto({
          resultType: CameraResultType.Base64,
          source: CameraSource.Camera,
          quality: 90,
        });
        const file = await fetch(`data:image/jpeg;base64,${photo.base64String}`).then((res) => res.blob());
        await handleImageUpload(file, mode);
      } catch (err) {
        console.error("Camera error:", err);
      }
    }
    setUploadIntent(null);
  };

  return (
    <div className="flex flex-col h-full px-4">
      <div className="flex-1 overflow-y-auto py-4">
        <div className="flex justify-between items-center mb-2 px-4">
          <h1 className="text-xl font-semibold">Chat</h1>
          <button
            onClick={resetConversation}
            className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700 transition"
            title="Start a new chat"
          >
            <ArrowPathIcon className="w-5 h-5" />
            <span className="sm:inline">New Chat</span>
          </button>
        </div>

        <div className="space-y-4">
          {messages.map((m, i) => {

          const isUserOrImage = m.role === "user" || m.role === "image";
          const isBotText = m.role === "assistant" || m.role === "model";
          const hasNewRecipes = Array.isArray(m.recipes) && m.recipes.length > 0;
          const hasLegacyRecipe = !!m.recipe; // for old role==="recipe" or assistant messages with .recipe

          return (
            <div
              key={i}
              ref={i === messages.length - 1 ? lastMessageRef : null}
              className={`flex ${isUserOrImage ? "justify-end" : "justify-start"}`}
            >
              {/* USER TEXT */}
              {m.role === "user" && (
                <div className="relative px-4 py-2 max-w-[80%] rounded-2xl bg-blue-100 text-left rounded-br-sm">
                  <Markdown content={m.content} />

                  {/* USER UPLOADED IMAGE */}
                  {m.image && (
                    <img
                      src={m.image}
                      alt="Uploaded"
                      className="w-full rounded-2xl shadow my-2"
                    />
                  )}
                </div>
              )}
        
              {/* OLD STYLE UPLOADED IMAGE */}
              {m.role === "image" && (
                <div className="relative px-4 py-2 max-w-[80%] sm:max-w-[40%] rounded-2xl bg-blue-100 text-left rounded-br-sm">
                  <img
                    src={m.content}
                    alt="Uploaded"
                    className="w-full rounded shadow mt-2"
                  />
                </div>
              )}
        
              {/* BOT TEXT + RECIPES */}
              {isBotText && (
                <div className="flex flex-col space-y-4">
                  {/* 1) free-form reply */}
                  {m.content && (
                    <div className="relative px-4 py-2 max-w-[80%] rounded-2xl bg-gray-100 text-left rounded-bl-sm">
                      <Markdown content={m.content} />

                      {/* ingredient list */}
                      { m.ingredients && (
                        <IngredientSelectMessage
                          ingredients={m.ingredients}
                          onSubmit={(selected) => {
                            const message = `Hey Sous, what can I cook with ${selected.join(
                              ", "
                            )}?`;
                            const newMessages = [
                              ...messages,
                              { role: "user", content: message },
                            ];
                            setMessages(newMessages);
                            setInput("");
                            setTimeout(() => sendMessage(message), 0);
                          }}
                        />
                      )}
                    </div>
                  )}
        
                  {/* 2) new-style recipes array */}
                  {hasNewRecipes &&
                    m.recipes.map((r, idx) => (
                      <RecipeMessage key={idx} recipe={r} />
                    ))}
        
                  {/* 3) legacy single recipe */}
                  {!hasNewRecipes && hasLegacyRecipe && (
                    <RecipeMessage recipe={m.recipe} />
                  )}
                </div>
              )}
        
              {/* PURE LEGACY “recipe” ROLE */}
              {m.role === "recipe" && <RecipeMessage recipe={m.recipe} />}
        
              {/* INGREDIENT-SELECT FLOW */}
              { m.role === "ingredient-select" && (
                <IngredientSelectMessage
                  ingredients={m.ingredients}
                  onSubmit={(selected) => {
                    const message = `Hey Sous, what can I cook with ${selected.join(
                      ", "
                    )}?`;
                    const newMessages = [
                      ...messages,
                      { role: "user", content: message },
                    ];
                    setMessages(newMessages);
                    setInput("");
                    setTimeout(() => sendMessage(message), 0);
                  }}
                />
              )}
            </div>
          );
        })}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl rounded-bl-sm p-3 w-fit max-w-[80%] text-left relative">
                <div className="loader"></div>
              </div>
            </div>
          )}
          {isImageLoading && (
            <div ref={imageLoaderRef} className="flex justify-end">
              <div className="relative bg-blue-100 rounded-2xl rounded-br-sm p-3 w-fit max-w-[80%] text-left">
                <div className="flex items-center gap-2">
                  <PhotoIcon className="h-8 w-8 text-gray-500 animate-pulse" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {warning && (
        <div className="text-sm text-yellow-800 bg-yellow-100 border border-yellow-300 px-3 py-2 rounded mb-4 mx-4">
          {warning}
          <button
            className="w-full bg-sage text-white rounded-3xl py-2 px-4 hover:scale-[1.02]"
            onClick={() => navigate("/subscribe")}
          >
            View subscription plans
          </button>
        </div>
      )}

      <div className="w-full bg-white z-10 pb-2">
        <div className="bg-white rounded-xl border border-gray-300 px-2 py-2 flex flex-col gap-2">
          <TextareaAutosize
            maxRows={4}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              const isMobile = /Mobi|Android/i.test(navigator.userAgent);
              if (!isMobile && e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Send a message"
            className="w-full resize-none bg-transparent p-2 focus:outline-none rounded"
          />

          <div className="flex items-center justify-between relative">
            <div className="relative flex items-center gap-2">
              <button
                onClick={() => {
                  setUploadIntent('gallery'); // ✅ changed
                  setShowUploadOptions(true); // ✅ changed
                }}
                className="border border-gray-300 rounded-2xl text-gray-600 flex items-center gap-1 text-sm px-2 py-1 cursor-pointer"
              >
                <PhotoIcon className="w-4 h-4" />
                <span>Upload image</span>
              </button>
              <button
                onClick={() => {
                  setUploadIntent('camera'); // ✅ changed
                  setShowUploadOptions(true); // ✅ changed
                }}
                className="border border-gray-300 rounded-2xl text-gray-600 flex items-center gap-1 text-sm px-2 py-1 cursor-pointer"
              >
                <CameraIcon className="w-4 h-4" />
                <span>Take photo</span>
              </button>

              {showUploadOptions && uploadIntent && (
                <div className="absolute bottom-full mb-2 left-0 bg-white border rounded-md shadow-lg p-2 flex gap-2 z-50">
                  <button
                    onClick={() => handleUserModeSelection('ingredients')} // ✅ changed
                    className="relative w-32 h-32 rounded-md overflow-hidden shadow group"
                  >
                    <img src="/assets/ingredient-search.png" alt="Ingredient Search" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-xs font-semibold text-center px-2">
                      Search Ingredients
                    </div>
                  </button>
                  <button
                    onClick={() => handleUserModeSelection('recipe')} // ✅ changed
                    className="relative w-32 h-32 rounded-md overflow-hidden shadow group"
                  >
                    <img src="/assets/recipe-search.png" alt="Recipe Search" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-xs font-semibold text-center px-2">
                      Find Recipe
                    </div>
                  </button>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                id="image-upload"
                hidden
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleImageUpload(file, uploadMode); // ✅ changed
                  }
                  setUploadIntent(null); // ✅ added
                }}
              />
            </div>
            <button
              onClick={() => sendMessage()}
              disabled={isLoading}
              className="bg-sage text-white p-2 rounded-md hover:bg-sage/90 disabled:opacity-50"
            >
              <PaperAirplaneIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}